import { AppState } from '../app.state';
import {
  signUp as signUpApi,
  confirmSignUp,
  signIn as signInApi,
  forgotPassword as fgPasswordApi,
  forgotPasswordSubmit as fgPasswordSubmitApi,
} from '../_shared/services/authentication.service';
import { AuthActions } from './authentication.reducer';
import { getAllRoles } from '../_shared/services/roles.service';
import {
  createUser,
  generatePinAndSendMsgToUser,
  getToken,
  getUser,
  updateUser as updateUserApi,
} from '../_shared/services/manage-users.service';
import { _User, User } from '../_shared/interfaces/user';
import { get, set } from 'lodash';
import moment from 'moment';
import localforage from 'localforage';
import { trackEvent } from '../_shared/services/analytics.service';
import eventTypes from '../_shared/interfaces/api/eventTypes';
import { encryptString } from '../utils';
import { isProd } from '../config';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Persistor } from 'redux-persist';

export const newSignUp = (user: User, password: string) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  try {
    if (!user.email) {
      throw new Error('No email provided');
    }

    const userExists = await getUser(user.email).catch(() => 0);

    if (userExists) {
      throw new Error(
        'User already exists. Please select Back to Sign In to log in to your account.'
      );
    }

    await createUser(user);

    await signUpApi(
      encryptString(JSON.stringify({ email: user.email })),
      password
    );
  } catch (err) {
    throw err;
  }
};

export const signUp = (username: string, password: string) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  const resp = await signUpApi(username, password);

  dispatch({
    type: AuthActions.SET_CURRENT_USER,
    payload: { email: username },
  });

  return resp ? true : false;
};

// AER-1669: On non-prod (QA/dev/local) only, let designated DentalEZ test
// accounts skip the role-0 email/SMS MFA pin, so the Playwright E2E suite
// (PR #672) can log the admin role in with a password alone — no emailed code
// to read. Safe because the pin is a purely client-side gate: Cognito has
// already authenticated by this point and the backend mints an identical token
// either way (POST /user/token signs whatever user object it's handed; there is
// no backend "2FA-completed" scope). This branch is dead code in the prod
// bundle, where isProd() is true. List mirrors the backend email-passthrough
// allowlist used for test accounts (server/src/controllers/email).
const MFA_BYPASS_EMAILS = ['@go-helios.com', '@parsed.io', 'genisi@gmail.com'];

const isMfaBypassed = (email?: string): boolean => {
  if (isProd() || !email) {
    return false;
  }
  const addr = email.toLowerCase();
  return MFA_BYPASS_EMAILS.some((entry) =>
    entry.startsWith('@') ? addr.endsWith(entry) : addr === entry
  );
};

export const signIn = (
  username: string,
  password: string,
  checkRole = true,
  setToActive = false,
  forcePin = false
) => async (dispatch: Dispatch<{}>, getState: () => AppState) => {
  try {
    const resp = await signInApi(username, password);

    const user = await getUser(username);

    if (
      forcePin ||
      (user.role === 0 && checkRole && !isMfaBypassed(username)) ||
      (user.isVerified === false && !setToActive)
    ) {
      const { method } = await generatePinAndSendMsgToUser(username);
      return { pin: true, method };
    }

    if (setToActive) {
      await updateUserApi({
        ...user,
        isVerified: true,
        isActive: 1,
      });
    }

    dispatch({
      type: AuthActions.SET_CURRENT_USER,
      payload: resp.attributes,
    });

    // AER-1589: clear any persisted token from a prior session before
    // getUserData → loadToken. loadToken's "skip if token exists" guard
    // (which preserves the 2FA-stepped scope across WebView reloads) would
    // otherwise keep the stale token, and the fresh /user/token call that
    // *should* mint a new 2FA-scoped token (because validateCode just ran)
    // never fires. Result: next 2FA-protected request 401s and the axios
    // interceptor bounces the user to /login.
    dispatch({
      type: AuthActions.SET_TOKEN,
      token: undefined,
    });

    if (resp) {
      await dispatch(getUserData());
      const _user = getState().auth.user;
      if (_user && !_user.isActive) {
        await localforage.clear();
        dispatch({
          type: AuthActions.LOG_USER_OUT,
        });
        throw new Error('User is Inactive. You have been logged out.');
      }
      trackEvent(eventTypes.log_in);

      // AER-1668: force the freshly-set auth slice (auth.user now carries sk
      // from getUserData above) to disk immediately. Without this the DEZ-auth
      // persist write can lag ~30-60s behind login — starved by the login-time
      // Pusher storm churning the larger DEZ-dash slice — so a page reload
      // inside that window rehydrates no auth.user and the gateway bounces the
      // still-authenticated user to /login. Best-effort only: a persistence
      // hiccup must never fail the login itself.
      try {
        const persistor = get(window, 'PERSISTOR') as Persistor | undefined;
        if (persistor) {
          await persistor.flush();
        }
      } catch (flushErr) {
        console.warn('[auth] flush after login failed', get(flushErr, 'message'));
      }
    }
    return resp ? true : false;
  } catch (err) {
    throw err;
  }
};

export const GUESTID = 'guest@dentalez.com';

export const isGuestUser = (sState: AppState) => {
  return get(sState, 'auth.user.sk') === GUESTID;
};

export const loginAsGuest = () => (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  const guestUser = new _User(({
    email: GUESTID,
    sk: GUESTID,
    userId: GUESTID,
    firstName: 'Guest',
    lastName: 'User',
    role: 7,
  } as unknown) as User);

  dispatch({
    type: AuthActions.SET_CURRENT_USER,
    payload: guestUser,
  });
};

export const getUserData = () => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  const sState = getState();
  const auth = sState.auth;

  if (auth.user) {
    try {
      await dispatch(loadToken());
      const allRoles = await getAllRoles();
      const user = await getUser(auth.user.email);
      dispatch({
        type: AuthActions.SET_CURRENT_USER,
        payload: {
          ...auth.user,
          ...user,
        },
      });
      return dispatch({ type: AuthActions.SET_ALL_ROLES, payload: allRoles });
    } catch (err) {
      console.warn(err);
      throw err;
    }
  }
};

// AER-1545: fetch a user fresh from the server and (when it's the signed-in
// user) update redux. Returns the fresh user so callers can re-seed local
// form state directly without waiting for a prop re-render. Used by the
// notification-settings screen on mount so the toggles always reflect what's
// actually persisted server-side, not a possibly-stale redux snapshot.
export const refreshUser = (email: string, setCurrent: boolean = true) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
): Promise<User | undefined> => {
  if (!email) { return undefined; }
  try {
    const user = await getUser(email);
    if (setCurrent) {
      const auth = getState().auth;
      dispatch({
        type: AuthActions.SET_CURRENT_USER,
        payload: { ...auth.user, ...user },
      });
    }
    return user;
  } catch (err) {
    console.warn('refreshUser failed', err);
    return undefined;
  }
};

export const updateUser = (user: User, shouldSetCurrent: boolean) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  const cUser = getState().auth.user as User;
  set(user, 'updatedBy', cUser.userId);
  set(user, 'updatedAt', moment().toISOString());
  try {
    const u = await updateUserApi({
      ...user,
      userId: user.userId,
    });
    if (shouldSetCurrent) {
      dispatch({ type: AuthActions.SET_CURRENT_USER, payload: u });
    }

    // AER-1545: return the updated user (server response) rather than the
    // current redux user. When an admin edits another user, redux still
    // holds the admin's record — callers (e.g. notification settings) need
    // the edited user's persisted state to refresh local form state.
    return shouldSetCurrent ? getState().auth.user : u;
  } catch (err) {
    console.warn(err);
    throw new Error(err);
  }
};

export const verify = (email: string, code: string) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  let resp: any = { attributes: {}, error: undefined };
  try {
    resp = await confirmSignUp(email, code);
  } catch (err) {
    resp.error = err;
  }
  return resp;
};

export const forgotPassword = (email: string) => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  return await fgPasswordApi(email);
};

export const forgotPasswordSubmit = (
  email: string,
  code: string,
  password: string
) => async (dispatch: Dispatch<{}>, getState: () => AppState) => {
  return await fgPasswordSubmitApi(email, code, password);
};

export const loadToken = () => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
) => {
  try {
    const sState = getState();
    const auth = sState.auth;

    // AER-1589: if we already have a token (restored from redux-persist on a
    // fresh page load), keep it. POST /user/token re-issues a base token,
    // which silently strips the 2FA-stepped scope from a super-admin token —
    // the next 2FA-protected request then 401s, the axios interceptor wipes
    // localforage + reloads, and the user gets bounced to /login. The 401
    // interceptor still handles genuine expiry; this just avoids voluntarily
    // downgrading an otherwise-valid scoped token on every cold mount.
    if (auth.token) {
      return;
    }

    const token = await getToken(auth.user || { temp: true });

    dispatch({
      type: AuthActions.SET_TOKEN,
      token,
    });
  } catch (err) {
    console.error('FAILED TO SET TOKEN', err.message);
  }
};

// AER-1668: cold-boot session restore. The login flow sets auth.user in redux,
// but the DEZ-auth persist write can lag ~30-60s behind login (see signIn /
// reducer.ts notes); a page reload inside that window rehydrates no auth.user,
// so the gateway's isLoggedIn() check fails and bounces the user to /login even
// though Cognito still holds a valid session. There is no other boot-time
// Cognito restore today — loadToken() hits POST /user/token, not Amplify. This
// thunk asks Amplify for the current session and, when the id token is present
// and unexpired, re-seeds redux with the SAME SET_CURRENT_USER the login flow
// uses (so auth.user.sk populates and isLoggedIn() passes). It must NEVER throw
// or block login: any failure / absent session resolves to false so the normal
// /login flow runs and the user is never trapped.
export const restoreSessionFromAmplify = () => async (
  dispatch: Dispatch<{}>,
  getState: () => AppState
): Promise<boolean> => {
  try {
    const session = await fetchAuthSession();
    const idToken = get(session, 'tokens.idToken');
    if (!idToken) {
      return false;
    }

    // Amplify auto-refreshes when the refresh token is still valid, so a
    // present-but-expired id token means the Cognito session is actually dead.
    const exp = get(idToken, 'payload.exp') as number | undefined;
    if (exp && exp * 1000 <= Date.now()) {
      return false;
    }

    let email = get(idToken, 'payload.email') as string | undefined;
    if (!email) {
      const attributes = await fetchUserAttributes();
      email = get(attributes, 'email') as string | undefined;
    }
    if (!email) {
      return false;
    }

    // Same service the login flow uses; getUser returns the record whose sk
    // makes isLoggedIn() pass once we put it in redux.
    const user = await getUser(email);
    if (!user || !get(user, 'sk')) {
      return false;
    }

    dispatch({
      type: AuthActions.SET_CURRENT_USER,
      payload: user,
    });

    // AER-1668: drop the cold-boot temp token. On a persist-lag boot loadToken
    // mints getToken({ temp: true }) before auth.user is known; clearing it
    // here (exactly as the login flow does) lets the subsequent getUserData →
    // loadToken re-issue a proper user-scoped backend token rather than leaving
    // the temp one in place and persisting it.
    dispatch({
      type: AuthActions.SET_TOKEN,
      token: undefined,
    });

    return true;
  } catch (err) {
    console.warn('[auth] restoreSessionFromAmplify failed', get(err, 'message'));
    return false;
  }
};
